import React from 'react';

/**
 * NormalCurveSVG
 * Props:
 *  - zScore?: number  // or use `z`
 *  - z?: number       // legacy alias
 *  - mean?: number    // default 0
 *  - sd?: number      // default 1
 *  - width?: number   // default 900
 *  - height?: number  // default 420
 *  - showBands?: boolean      // default true
 *  - showGrid?: boolean       // default true
 *  - showTScoreAxis?: boolean // default true
 *  - uiScale?: number         // default 1.2
 *  - responsive?: boolean     // default true
 *  - className?: string
 *  - style?: React.CSSProperties
 */
export default function NormalCurveSVG(props) {
	const {
		zScore,
		z, // legacy alias
		mean = 0,
		sd = 1,
		width = 900,
		height = 420,
		showBands = true,
		showGrid = true,
		showTScoreAxis = true,
		shadePercentile = false,
		markerColors,
		uiScale = 1.2,
		responsive = true,
		className,
		style,
	} = props;

	// ---------- math ----------
	const rawZ = Number.isFinite(zScore)
		? +zScore
		: Number.isFinite(z)
			? +z
			: 0;
	const saneSd = sd > 0 ? sd : 1;

	const invSqrt2Pi = 1 / Math.sqrt(2 * Math.PI);
	const pdf = (x) => invSqrt2Pi * Math.exp(-0.5 * x * x);

	// ---------- layout / scales ----------
	const margin = {
		top: 48,
		right: 40,
		bottom: showTScoreAxis ? 120 : 68,
		left: 40,
	};
	const W = width - margin.left - margin.right;
	const H = height - margin.top - margin.bottom;

	const xMin = -4,
		xMax = 4; // show ±4
	const yMax = pdf(0); // curve peak

	const x2px = (x) => margin.left + ((x - xMin) / (xMax - xMin)) * W;
	const y2px = (y) => margin.top + (1 - y / yMax) * H;

	// sample the curve
	const SAMPLES = 700;
	const pts = Array.from({length: SAMPLES}, (_, i) => {
		const x = xMin + (i / (SAMPLES - 1)) * (xMax - xMin);
		return {x, y: pdf(x)};
	});
	const curvePath = pts
		.map((p, i) => `${i ? 'L' : 'M'}${x2px(p.x)},${y2px(p.y)}`)
		.join(' ');

	// ticks at integer z
	const ticks = [-4, -3, -2, -1, 0, 1, 2, 3, 4];

	// standardized z
	const zStd = (rawZ - mean) / saneSd;
	const clampZ = Math.max(xMin, Math.min(xMax, zStd));
	const zx = x2px(clampZ);
	const zy = y2px(pdf(clampZ));

	// ---------- sizes (scaled) ----------
	const tickLen = 10 * uiScale;
	const tickFont = 16 * uiScale;
	const curveStroke = 2.6 * uiScale;
	const axisStroke = 1.2 * uiScale;
	const gridStroke = 0.8 * uiScale;
	const zStroke = 3.2 * uiScale;
	const zDotR = 4.8 * uiScale;
	const calloutFont = 16 * uiScale;

	// headroom for label above curve
	const headroom = 22 * uiScale;
	const zTopY = Math.max(6, margin.top - headroom);

	// callout placement and clamping (avoid overflow)
	const calloutSide = clampZ < 0 ? 'right' : 'left';
	const calloutDx = 12 * uiScale;
	const rawCalloutX =
		calloutSide === 'right' ? zx + calloutDx : zx - calloutDx;
	const calloutX = Math.min(
		width - margin.right - 4,
		Math.max(margin.left + 4, rawCalloutX)
	);
	const calloutAnchor = calloutSide === 'right' ? 'start' : 'end';

	const labelYTop = Math.max(4, zTopY - calloutFont - 4);

	// tick label formatter (NO σ)
	const formatZTick = (t) => (t === 0 ? '0' : `${t}`); // -> -4 -3 -2 -1 0 1 2 3 4

	const defaultMarkerColors = {
		low: '#dc2626', // red
		mid: '#1d4ed8', // dark blue
		high: '#16a34a', // green
	};

	const bucket = clampZ >= 1 ? 'high' : clampZ <= -1 ? 'low' : 'mid';
	const palette = markerColors || defaultMarkerColors;
	const markerColor = palette[bucket] || defaultMarkerColors[bucket];

	// T-score mapping (classic): T = 50 + 10z
	const tTickZs = [-3, -2, -1, 0, 1, 2, 3];
	const tScoreFromZ = (zz) => Math.round(50 + 10 * zz);

	// band polygons
	const bandRanges = [
		{from: xMin, to: -2, className: 'band band--neg2'},
		{from: -2, to: -1, className: 'band band--neg1'},
		{from: -1, to: 1, className: 'band band--mid'},
		{from: 1, to: 2, className: 'band band--pos1'},
		{from: 2, to: xMax, className: 'band band--pos2'},
	];
	const bandPath = (from, to) => {
		const seg = pts.filter((p) => p.x >= from && p.x <= to);
		if (!seg.length) return null;
		const top = seg
			.map((p, i) => `${i ? 'L' : 'M'}${x2px(p.x)},${y2px(p.y)}`)
			.join(' ');
		const base = `L${x2px(to)},${y2px(0)} L${x2px(from)},${y2px(0)} Z`;
		return top + ' ' + base;
	};

	// responsive sizing
	const svgSizing = responsive
		? {
				style: {
					width: '100%',
					height: 'auto',
					display: 'block',
					...(style || {}),
				},
				className,
			}
		: {width, height, style, className};

	// baseline Y
	const zAxisY = y2px(0);

	// T-axis placement (below z-axis labels)
	const tAxisY = zAxisY + tickLen + 44 * uiScale;
	const tLabelY = tAxisY + tickLen + 16 * uiScale;

	return (
		<svg
			{...svgSizing}
			viewBox={`0 0 ${width} ${height}`}
			preserveAspectRatio="xMidYMid meet"
			role="img"
			aria-label={`Z-score ${Number(rawZ).toFixed(2)}`}
		>
			{/* background */}
			<rect x="0" y="0" width={width} height={height} className="bc-bg" />

			{/* colored bands */}
			{showBands &&
				bandRanges.map((b, idx) => {
					const d = bandPath(b.from, b.to);
					const isActive = clampZ >= b.from && clampZ <= b.to;
					return d ? (
						<path
							key={idx}
							d={d}
							className={b.className}
							style={{opacity: isActive ? 0.22 : 0.12}}
						/>
					) : null;
				})}

			{/* optional vertical grid at ticks */}
			{shadePercentile &&
				(() => {
					const to = clampZ;
					const seg = pts.filter((p) => p.x >= xMin && p.x <= to);
					if (seg.length < 2) return null;

					const top = seg
						.map(
							(p, i) =>
								`${i ? 'L' : 'M'}${x2px(p.x)},${y2px(p.y)}`
						)
						.join(' ');
					const base = `L${x2px(to)},${y2px(0)} L${x2px(xMin)},${y2px(0)} Z`;

					return (
						<path
							d={top + ' ' + base}
							style={{fill: markerColor, opacity: 0.08}}
						/>
					);
				})()}

			{showGrid &&
				ticks.map((t, i) => (
					<line
						key={`g${i}`}
						x1={x2px(t)}
						y1={margin.top}
						x2={x2px(t)}
						y2={margin.top + H}
						className="bc-grid"
						style={{stroke: '#e5e7eb', strokeWidth: gridStroke}}
					/>
				))}

			{/* curve */}
			<path
				d={curvePath}
				className="bc-curve"
				style={{strokeWidth: curveStroke}}
			/>

			{/* baseline (z-axis line) */}
			<line
				x1={x2px(xMin)}
				y1={zAxisY}
				x2={x2px(xMax)}
				y2={zAxisY}
				className="bc-axis"
				style={{strokeWidth: axisStroke}}
			/>

			{/* z-axis ticks + labels (NO σ) */}
			{ticks.map((t, i) => (
				<g key={`t${i}`}>
					<line
						x1={x2px(t)}
						y1={zAxisY}
						x2={x2px(t)}
						y2={zAxisY + tickLen}
						className="bc-tick"
						style={{strokeWidth: axisStroke}}
					/>
					<text
						x={x2px(t)}
						y={zAxisY + tickLen + 16 * uiScale}
						className="bc-tick-label"
						textAnchor="middle"
						style={{fontSize: tickFont}}
					>
						{formatZTick(t)}
					</text>
				</g>
			))}

			{/* T-score axis (underneath) */}
			{showTScoreAxis && (
				<>
					{/* axis line */}
					<line
						x1={x2px(xMin)}
						y1={tAxisY}
						x2={x2px(xMax)}
						y2={tAxisY}
						className="bc-axis bc-axis--t"
						style={{strokeWidth: axisStroke, opacity: 0.8}}
					/>

					{/* optional left label "T" */}
					<text
						x={margin.left}
						y={tLabelY}
						textAnchor="start"
						className="bc-t-label"
						style={{fontSize: 14 * uiScale, opacity: 0.7}}
					>
						T
					</text>

					{/* T ticks at z = -3..+3 */}
					{tTickZs.map((zz, i) => (
						<g key={`tt${i}`}>
							<line
								x1={x2px(zz)}
								y1={tAxisY}
								x2={x2px(zz)}
								y2={tAxisY + tickLen}
								className="bc-tick bc-tick--t"
								style={{strokeWidth: axisStroke, opacity: 0.9}}
							/>
							<text
								x={x2px(zz)}
								y={tLabelY}
								className="bc-tick-label bc-tick-label--t"
								textAnchor="middle"
								style={{fontSize: 14 * uiScale, opacity: 0.85}}
							>
								{tScoreFromZ(zz)}
							</text>
						</g>
					))}
				</>
			)}

			{/* z marker */}
			<line
				x1={zx}
				y1={zAxisY}
				x2={zx}
				y2={zTopY}
				className="bc-zline"
				style={{strokeWidth: zStroke, stroke: markerColor}}
			/>
			{showTScoreAxis && (
				<line
					x1={zx}
					y1={zAxisY}
					x2={zx}
					y2={tAxisY}
					style={{stroke: markerColor, strokeWidth: 2, opacity: 0.35}}
				/>
			)}
			<circle
				cx={zx}
				cy={zy}
				r={zDotR}
				className="bc-zdot"
				style={{fill: markerColor, stroke: '#fff', strokeWidth: 2}}
			/>

			{showTScoreAxis && (
				<circle
					cx={zx}
					cy={tAxisY}
					r={3.2 * uiScale}
					style={{
						fill: markerColor,
						stroke: '#fff',
						strokeWidth: 1.5,
					}}
				/>
			)}

			{/* callout above the line (NO percentile) */}
			<text
				x={calloutX}
				y={labelYTop}
				textAnchor={calloutAnchor}
				dominantBaseline="hanging"
				className="bc-callout"
				style={{
					fontSize: calloutFont,
					fontWeight: 600,
					fill: markerColor,
				}}
			>
				z = {Number(rawZ).toFixed(2)}
			</text>
		</svg>
	);
}
